import { SelectItem } from 'primeng';
import { TaskCollectionTravelMode } from '../../enums/task-collection-travel-mode.enum';
import { computed, observable } from 'mobx-angular';
import { IVehicle, IVehicleAddress } from '../../interfaces/task-info';
import { HourBandPipe } from '../../pipes/hour-band.pipe';
import {
  TaskScheduleDTO,
  UpdateTaskScheduleDTO,
  JobTaskSupplierScheduleContextTask,
} from './task-supplier-schedule';
import { NoneSameDayDeliveryReason } from '../../interfaces/none-same-day-delivery-reason';
import { add } from 'date-fns';
import { FormControl, Validators } from '@angular/forms';
import { EventEmitter } from '@angular/core';
import { IOrgStepChecklistSubCategories } from '../../interfaces/organisation-specific-steps';
import { addHours } from '../../utils/date-utils';

export interface ITaskSupplierScheduleStoreTask {
  id: number;
  jobId: number;

  pickupEarliestDateTime: string;
  pickupLatestDateTime: string;

  dropoffEarliestDateTime: string;
  dropoffLatestDateTime: string;

  taskSupplierSchedule?: TaskScheduleDTO;

  estimateTime: string;

  vehicle: IVehicle;

  pickupLocation: IVehicleAddress;
  dropoffLocation: IVehicleAddress;

  estimateTimeHHmm?: string;
  distance?: number;

  taskTypeId?: number;
  taskTypeName: string;
  carryOverAuthorised?: boolean;

  checklistSubCategories: IOrgStepChecklistSubCategories[];
}

/**
 * Encapsulates the task supplier schedule (pick-up and drop-off) for
 * a particular task
 */
export class TaskSupplierScheduleStore {
  @observable pickUp: TaskSupplierScheduleStepStore;
  @observable dropOff: TaskSupplierScheduleStepStore;

  /**
   * The task associated with this schedule
   */
  @observable task: ITaskSupplierScheduleStoreTask;

  /**
   * A list options for specifying the reason the drop-off date is not the
   * same day as the pick-up date.
   */
  noneSameDayDeliveryReasons: NoneSameDayDeliveryReason[] = [];

  /**
   * default travel mode
   */
  defaultTravelMode: TaskCollectionTravelMode;

  /**
   * Currently selected id from the noneSameDayDeliveryReasons above
   */
  @observable noneSameDayDeliveryReasonId: number = null;

  public pickupScheduledTimeChanged: EventEmitter<Date> = new EventEmitter<Date>();
  public dropOffScheduledTimeChanged: EventEmitter<Date> = new EventEmitter<Date>();

  constructor(
    travelSchedulerContext: JobTaskSupplierScheduleContextTask,
    noneSameDayDeliveryReasons: NoneSameDayDeliveryReason[],
  ) {
    const schedule = travelSchedulerContext.currentSchedule;

    this.pickUp = new TaskSupplierScheduleStepStore(false, travelSchedulerContext, schedule);
    this.dropOff = new TaskSupplierScheduleStepStore(true, travelSchedulerContext, schedule);

    this.task = travelSchedulerContext;
    this.task.taskSupplierSchedule = schedule;

    this.defaultTravelMode = TaskCollectionTravelMode.Driving;

    this.noneSameDayDeliveryReasons = noneSameDayDeliveryReasons;

    if (noneSameDayDeliveryReasons.length > 0) {
      this.noneSameDayDeliveryReasonId = noneSameDayDeliveryReasons[0].id;
      this.noneSameDayReasonControl.setValue(this.noneSameDayDeliveryReasonId);
    }

    this.pickUp.scheduledTimeChanged.subscribe((e) => this.pickupScheduledTimeChanged.emit(e));
    this.dropOff.scheduledTimeChanged.subscribe((e) => this.dropOffScheduledTimeChanged.emit(e));
  }

  public noneSameDayReasonControl = new FormControl(null, Validators.required);

  @computed
  public get isSameDayDelivery(): boolean {
    return this.pickUp.scheduledTimeDay === this.dropOff.scheduledTimeDay;
  }

  @computed
  public get noneSameDayDeliveryReason(): NoneSameDayDeliveryReason {
    return this.noneSameDayDeliveryReasons.find((x) => x.id === this.noneSameDayDeliveryReasonId);
  }

  @computed
  public get noneSameDayDeliveryReasonOptions(): SelectItem[] {
    return this.noneSameDayDeliveryReasons.map((r) => ({
      label: r.reason,
      value: r.id,
    }));
  }

  @computed
  public get scheduledPickupPlusJourneyTime(): Date {
    return TaskSupplierScheduleStore.getPickupPlusJourneyTime(this.task, this.pickUp.scheduledTime);
  }

  static getPickupPlusJourneyTime(task: ITaskSupplierScheduleStoreTask, pickupTime?: Date): Date {
    pickupTime = pickupTime || new Date(task.pickupEarliestDateTime);
    if (!task.estimateTime) {
      return pickupTime;
    }
    const parts = task.estimateTime.split(':');
    if (parts.length != 3) {
      return pickupTime;
    }

    const hoursToAdd = Number(parts[0]);
    //The reason to add is because in drop off , the timeband is + 1

    let z = add(pickupTime, { hours: hoursToAdd + 1 });

    return z;
  }

  @computed
  public get noneSameDayDeliveryReasonValid(): boolean {
    return this.isSameDayDelivery || this.noneSameDayDeliveryReasonId != null;
  }
}

export enum TaskSupplierScheduleSource {
  JobDeployment = 0,
  GrabJob = 1,
  DriverAllocation = 2,
  SupplierEdit = 3,
  EngineiusEdit = 4,
}

/**
 * Encapsulates the supplier schedule for either the pick-up or drop-off
 * specifically
 */
export class TaskSupplierScheduleStepStore {
  /**
   * Flag indicating whether this schedule has been explicitly set
   * or generated from the collection/delivery window times
   */
  isDefault: boolean = false;

  public get hasSchedule(): boolean {
    return !!this.task.taskSupplierSchedule;
  }

  /**
   * Whether this schedule is for drop-off (or pick-up)
   */
  isDropOff: boolean;

  /**
   * A list of options of how the driver will be travelling to the pick-up location
   * not used for drop-off
   */
  travelModeOptions: SelectItem[] = [
    {
      title: 'Driving',
      label: '',
      value: TaskCollectionTravelMode.Driving,
      icon: 'ui-icon-directions-car',
    },
    {
      title: 'Public Transport',
      label: '',
      value: TaskCollectionTravelMode.PublicTransport,
      icon: 'ui-icon-directions-bus',
    },
    {
      title: 'Walking',
      label: '',
      value: TaskCollectionTravelMode.Walking,
      icon: 'ui-icon-directions-walk',
    },
    {
      title: 'Cycling',
      label: '',
      value: TaskCollectionTravelMode.Cycling,
      icon: 'ui-icon-directions-bike',
    },
  ];

  public scheduledTimeChanged: EventEmitter<Date> = new EventEmitter<Date>();
  public scheduledTimeLatestChanged: EventEmitter<Date> = new EventEmitter<Date>();

  /**
   * Currently selected from the options above
   */
  @observable travelMode: TaskCollectionTravelMode | null = null;

  /**
   * The task associated with this schedule
   */
  @observable task: ITaskSupplierScheduleStoreTask;

  /**
   * The scheduled time for pick-up/drop-off
   */
  private _scheduledTime: Date;

  private _scheduledTimeLatest: Date;

  /**
   * Below 2 only used when the date is past today
   */
  /**
   * If the date is past present day, then we need to have a variable to find min and max
   */
  private _minPastDate: Date;

  /**
   * If the date is past present day, then we need to have a variable to find min and max
   */
  private _maxPastDate: Date;

  private readonly closing_time = 19;

  public get minPastDate(): Date {
    return this._minPastDate;
  }

  public get maxPastDate(): Date {
    return this._maxPastDate;
  }

  /**
   * The scheduled time for pick-up/drop-off
   */
  public get scheduledTime(): Date {
    return this._scheduledTime;
  }

  public set scheduledTime(value: Date) {
    if (value && value.getTime() != this._scheduledTime?.getTime()) {
      this.scheduledTimeChanged.emit(value);
      this._scheduledTime = new Date(value);
    }
  }

  public get scheduledTimeLatest(): Date {
    return this._scheduledTimeLatest;
  }

  public set scheduledTimeLatest(value: Date) {
    if (value && value.getTime() != this._scheduledTimeLatest?.getTime()) {
      this.scheduledTimeLatestChanged.emit(value);
      this._scheduledTimeLatest = value;
    }
  }

  /**
   * The source context of where the schedule is being
   * modified from
   */
  @observable source: TaskSupplierScheduleSource = TaskSupplierScheduleSource.JobDeployment;

  @computed
  public get windowStart(): Date {
    return this.getTomorrowWindowStartDate();
  }

  @computed
  public get windowEnd(): Date {
    return this.getTomorrowWindowEndDate();
  }

  private getTomorrowWindowEndDate(): Date {
    const dropoffDateTime = new Date(this.task.dropoffLatestDateTime);
    const now = new Date();

    if (dropoffDateTime.getTime() < now.getTime()) {
      return new Date(
        this.getMaxDateRange(new Date(this.scheduledTime)).setHours(this.closing_time, 0, 0, 0),
      ); // End of the day
    }

    return this.isDropOff
      ? new Date(this.task.dropoffLatestDateTime)
      : new Date(this.task.pickupLatestDateTime);
  }

  private getTomorrowWindowStartDate(): Date {
    const dropoffDateTime = new Date(this.task.dropoffLatestDateTime);
    const now = new Date();

    if (dropoffDateTime.getTime() < now.getTime()) {
      return new Date(new Date(this.scheduledTime).setHours(0, 0, 0, 0)); // Start of the day
    }

    return this.isDropOff
      ? new Date(this.task.dropoffEarliestDateTime)
      : new Date(this.task.pickupEarliestDateTime);
  }

  @computed
  public get isScheduledTimeInWindow(): boolean {
    return this.scheduledTime >= this.windowStart && this.scheduledTime <= this.windowEnd;
  }

  @computed
  public get selectedTravelModeOption(): SelectItem {
    return this.travelModeOptions.find((x) => x.value == this.travelMode);
  }

  @computed
  public get scheduledTimeDay(): number {
    return new Date(this.scheduledTime).getDate();
  }

  @computed
  public get scheduledTimeHour(): number {
    let hour = new Date(this.scheduledTime).getHours();

    // Because we have wrapped over to the next day, but there is no 0 hour
    if (hour == 0) {
      hour = 1;
    }
    return hour;
  }

  /**
   * For drop off, we add an hour otherwise leave it as starting hour
   */
  @computed
  public get timeBandSelectOptions(): SelectItem[] {
    return Array.from(Array(24).keys()).map((hour) => ({
      label: new HourBandPipe().transform(hour),
      value: hour,
      styleClass: 'text-orange-600',
    }));
  }

  @computed
  public get scheduledTimeStatus(): 'Early' | 'Late' | 'On-time' {
    if (this.scheduledTime < this.windowStart) {
      return 'Early';
    } else if (this.scheduledTime > this.windowEnd) {
      return 'Late';
    }
    return 'On-time';
  }

  @computed
  public get validationText(): string {
    let action = this.isDropOff ? 'Delivery' : 'Collection';
    return `${this.scheduledTimeStatus} ${action}`;
  }

  constructor(
    isDropOff: boolean,
    task: ITaskSupplierScheduleStoreTask,
    taskSchedule: TaskScheduleDTO,
  ) {
    this.isDropOff = isDropOff;
    this.task = task;

    this.travelMode =
      task.taskSupplierSchedule?.taskCollectionTravelMode ?? taskSchedule?.taskCollectionTravelMode;

    let ts = task.taskSupplierSchedule || taskSchedule;

    this.isDefault = !ts;

    this.scheduledTime = this.isDropOff
      ? new Date(ts?.specifiedDropOffTime || task.dropoffLatestDateTime)
      : new Date(ts?.specifiedPickUpTime || task.pickupEarliestDateTime);

    // Will use specified latest time if set, otherwise will fall back on specified pickup +1 hour, or just customer windows.
    this.scheduledTimeLatest = this.isDropOff
      ? new Date(
          ts?.specifiedDropOffLatestTime ??
            addHours(ts?.specifiedDropOffTime, 1) ??
            task.dropoffLatestDateTime,
        )
      : new Date(
          ts?.specifiedPickUpLatestTime ??
            addHours(ts?.specifiedPickUpTime, 1) ??
            task.pickupEarliestDateTime,
        );

    if (this.isDropOff) {
      this.updateMaxPastDate();
    } else {
      if (new Date(task.pickupLatestDateTime).getTime() < Date.now()) {
        const presentDay = new Date(this.scheduledTime);
        this._minPastDate = new Date(presentDay.setHours(0, 0, 0));
        this._maxPastDate = new Date(this.getMaxDateRange(presentDay).setHours(this.closing_time));
      }
    }
  }

  public updateMaxPastDate() {
    if (new Date(this.task.dropoffLatestDateTime).getTime() < Date.now()) {
      const presentDay = new Date(this.scheduledTime);
      this._minPastDate = new Date(presentDay.setHours(0, 0, 0));
      this._maxPastDate = new Date(this.getMaxDateRange(presentDay).setHours(this.closing_time));
    }
  }

  private getMaxDateRange(date: Date): Date {
    const dayOffsets = {
      5: 3, // Friday
      6: 2, // Saturday
      0: 1, // Sunday
    };

    const dayOffset = dayOffsets[date.getDay()];
    if (dayOffset !== undefined) {
      date.setDate(date.getDate() + dayOffset);
    }

    return date;
  }
}
